import { assign, capitalize, clone, cloneDeep, find, get, maxBy, sortBy } from 'lodash'
import moment from 'moment'

import Access from '../../Access'

const MAX_MATCH_MINUTES = 120

export default class Match {
  calculateValues() {
    // Score based on events only when events and actions
    if ((this.events && this.events.length) || (this.actions && this.actions.length)) {
      this.events = Match.sortEvents(this.events)

      const score = this.scoreFromEvents(this.events)
      this.local_definition_goals = score.localDefinitionScore
      this.local_goals = score.localScore
      this.visitor_definition_goals = score.visitorDefinitionScore
      this.visitor_goals = score.visitorScore

      const cards = this.cardsFromEvents(this.events)
      this.local_cards_blue = cards.localCardsBlue
      this.local_cards_green = cards.localCardsGreen
      this.local_cards_red = cards.localCardsRed
      this.local_cards_yellow = cards.localCardsYellow
      this.visitor_cards_blue = cards.visitorCardsBlue
      this.visitor_cards_green = cards.visitorCardsGreen
      this.visitor_cards_red = cards.visitorCardsRed
      this.visitor_cards_yellow = cards.visitorCardsYellow
    }

    if (this.reports) {
      this.reports = Match.sortEvents(this.reports)
    }
    // Score based on actions
    this.actions = this.getMergedActions()

    const score = this.scoreFromEvents(this.actions)
    this.actions_local_definition_goals = score.localDefinitionScore
    this.actions_local_goals = score.localScore
    this.actions_visitor_definition_goals = score.visitorDefinitionScore
    this.actions_visitor_goals = score.visitorScore

    const cards = this.cardsFromEvents(this.actions)
    this.actions_local_cards_blue = cards.localCardsBlue
    this.actions_local_cards_green = cards.localCardsGreen
    this.actions_local_cards_red = cards.localCardsRed
    this.actions_local_cards_yellow = cards.localCardsYellow
    this.actions_visitor_cards_blue = cards.visitorCardsBlue
    this.actions_visitor_cards_green = cards.visitorCardsGreen
    this.actions_visitor_cards_red = cards.visitorCardsRed
    this.actions_visitor_cards_yellow = cards.visitorCardsYellow

    this.calculated = true
  }

  cardsFromEvents(events) {
    let localCardsBlue = 0
    let localCardsGreen = 0
    let localCardsRed = 0
    let localCardsYellow = 0
    let visitorCardsBlue = 0
    let visitorCardsGreen = 0
    let visitorCardsRed = 0
    let visitorCardsYellow = 0
    const eventsList = events || []

    eventsList.forEach((event) => {
      const isLocal = event.team.id === this.local?.id
      const isCard = event.type.indexOf('CARD') === 0

      // undefined when events, true/false when reports
      if (event.confirm !== false && isCard) {
        if (isLocal) {
          if (event.type === 'CARD_RED' || event.type === 'CARD_RED_DIRECT') {
            localCardsRed += 1
          } else if (event.type === 'CARD_YELLOW') {
            localCardsYellow += 1
          } else if (event.type === 'CARD_BLUE') {
            localCardsBlue += 1
          } else if (event.type === 'CARD_GREEN') {
            localCardsGreen += 1
          }
        } else if (event.type === 'CARD_RED' || event.type === 'CARD_RED_DIRECT') {
          visitorCardsRed += 1
        } else if (event.type === 'CARD_YELLOW') {
          visitorCardsYellow += 1
        } else if (event.type === 'CARD_BLUE') {
          visitorCardsBlue += 1
        } else if (event.type === 'CARD_GREEN') {
          visitorCardsGreen += 1
        }
      }

      assign(event, {
        localCardsBlue,
        localCardsGreen,
        localCardsRed,
        localCardsYellow,
        visitorCardsBlue,
        visitorCardsGreen,
        visitorCardsRed,
        visitorCardsYellow,
      })
    })

    return {
      localCardsBlue,
      localCardsGreen,
      localCardsRed,
      localCardsYellow,
      visitorCardsBlue,
      visitorCardsGreen,
      visitorCardsRed,
      visitorCardsYellow,
    }
  }

  static colorDistance(color1, color2) {
    const rgba1 = color1.split(',')
    const rgba2 = color2.split(',')
    let distance = 0

    rgba1.forEach((component, index) => {
      if (!rgba2[index]) {
        rgba2[index] = 0
      }
      distance += (parseInt(rgba2[index], 10) - parseInt(component, 10)) ** 2
    })

    return distance
  }

  constructor(configMatch) {
    assign(this, clone(configMatch), {
      approvedAt: undefined,
      endedAt: undefined,
      matchedAt: undefined,
      startedAt: undefined,
    })
    this.access = new Access(this.access)
    this.calculateValues()
  }

  get date() {
    if (this.isDeferred) {
      return null
    }
    return this.dateStarted || this.dateMatched || undefined
  }

  get dateApproved() {
    if (this.approvedAt === undefined) {
      this.approvedAt = this.approved_at ? moment.unix(this.approved_at) : null
    }
    return this.approvedAt
  }

  get dateEnded() {
    if (this.endedAt === undefined) {
      this.endedAt = this.ended_at ? moment.unix(this.ended_at) : null
    }
    return this.endedAt
  }

  dateForHumans(date = undefined) {
    if (date === -1 || (date === undefined && this.isDeferred)) {
      return 'Partido pospuesto'
    }
    if (date !== undefined && !date) {
      return '* Fecha sin definir'
    }
    return this.dateFormated(date) || '* Fecha sin definir'
  }

  dateFormated(date = this.date, format = 'ddd, DD [de] MMM YYYY HH:mm') {
    return date ? capitalize(date.format(format)) : ''
  }

  get dateMatched() {
    if (this.matchedAt === undefined) {
      this.matchedAt = this.matched_at ? moment.unix(this.matched_at) : null
    }
    return this.matchedAt
  }

  get dateStarted() {
    if (this.startedAt === undefined) {
      this.startedAt = this.started_at ? moment.unix(this.started_at) : null
    }
    return this.startedAt
  }

  static filterEvents(events, filter) {
    const filteredEvents = []
    let filterFunction = () => true
    if (filter) {
      if (typeof filter === 'string') {
        const regEx = new RegExp(filter)
        filterFunction = (e) => regEx.test(e.type)
      } else if (filter instanceof RegExp) {
        filterFunction = (e) => filter.test(e.type)
      } else {
        filterFunction = (e) => filter.indexOf(e.type) >= 0
      }
    }

    events.forEach((e) => {
      if (filterFunction(e)) {
        filteredEvents.push({
          id: e.id,
          confirm: e.confirm === undefined ? true : !!e.confirm,
          lat: null,
          lng: null,
          localCardsBlue: e.localCardsBlue,
          localCardsGreen: e.localCardsGreen,
          localCardsRed: e.localCardsRed,
          localCardsYellow: e.localCardsYellow,
          localDefinitionScore: e.localDefinitionScore,
          localScore: e.localScore,
          minute: Math.floor(e.second / 60),
          picture: null,
          player: cloneDeep(e.player),
          player_id: get(e.player, 'id'),
          report_id: e.report_id || e.id,
          second: e.second,
          team: cloneDeep(e.team),
          team_id: e.team.id,
          type: e.type,
          visitorCardsBlue: e.visitorCardsBlue,
          visitorCardsGreen: e.visitorCardsGreen,
          visitorCardsRed: e.visitorCardsRed,
          visitorCardsYellow: e.visitorCardsYellow,
          visitorDefinitionScore: e.visitorDefinitionScore,
          visitorScore: e.visitorScore,
        })
      }
    })
    return filteredEvents
  }

  getActions(filter) {
    return Match.filterEvents(this.actions, filter)
  }

  getEvents(filter) {
    return Match.filterEvents(this.events, filter)
  }

  getMergedActions() {
    const actions = cloneDeep(this.events || [])
    const reports = this.reports || []

    reports.forEach((report) => {
      const id = report.report_id || report.id
      const found = find(actions, { report_id: id })
      if (found) {
        assign(found, report)
      } else {
        const newReport = cloneDeep(report)
        if (this.local?.id === newReport.team_id) {
          newReport.team = this.local
        } else if (this.visitor.id === newReport.team_id) {
          newReport.team = this.visitor
        } else {
          // Some how this report does not match
          return
        }
        actions.push(newReport)
      }
    })

    return Match.sortEvents(actions)
  }

  getMostDistantColors() {
    const { local, visitor } = this
    const distances = [
      {
        distance: Match.colorDistance(local.color_primary || '0,0,0', visitor.color_primary || '255,255,255'),
        local: local.color_primary || '0,0,0',
        visitor: visitor.color_primary || '255,255,255',
      },
      {
        distance: Match.colorDistance(local.color_primary || '0,0,0', visitor.color_secondary || '255,255,255'),
        local: local.color_primary || '0,0,0',
        visitor: visitor.color_secondary || '255,255,255',
      },
      {
        distance: Match.colorDistance(local.color_secondary || '0,0,0', visitor.color_primary || '255,255,255'),
        local: local.color_secondary || '0,0,0',
        visitor: visitor.color_primary || '255,255,255',
      },
      {
        distance: Match.colorDistance(local.color_secondary || '0,0,0', visitor.color_secondary || '255,255,255'),
        local: local.color_secondary || '0,0,0',
        visitor: visitor.color_secondary || '255,255,255',
      },
    ]

    const maxDistance = maxBy(distances, 'distance')

    return [maxDistance.local, maxDistance.visitor]
  }

  getReports(filter) {
    return Match.filterEvents(this.reports, filter)
  }

  get isApproved() {
    return !!this.approved_at
  }

  get isBothLose() {
    return this.is_both_lose
  }

  get isDeferred() {
    return this.is_deferred
  }

  get isEnded() {
    return !!this.ended_at || this.minutes >= MAX_MATCH_MINUTES
  }

  get isWalkover() {
    return this.local_walkover || this.visitor_walkover
  }

  get minutes() {
    const { time } = this
    return time > 0 ? Math.floor(time / 60) : 0
  }

  scoreFromEvents(events) {
    let localDefinitionScore = 0
    let localScore = 0
    let visitorDefinitionScore = 0
    let visitorScore = 0
    const eventsList = events || []

    eventsList.forEach((event) => {
      const isLocal = event.team.id === this.local?.id
      const isGoal = event.type.indexOf('GOAL') === 0
      const isDefinition = event.type === 'PENAL_DEFINITION'

      // undefined when events, true/false when reports
      if (event.confirm !== false) {
        if (isGoal) {
          if (isLocal) {
            localScore += 1
          } else {
            visitorScore += 1
          }

          if (event.type === 'GOAL_AUTO') {
            if (isLocal) {
              visitorScore += 1
              localScore -= 1
            } else {
              localScore += 1
              visitorScore -= 1
            }
          }
        } else if (isDefinition) {
          if (isLocal) {
            localDefinitionScore += 1
          } else {
            visitorDefinitionScore += 1
          }
        }
      }
      assign(event, {
        localDefinitionScore,
        localScore,
        visitorDefinitionScore,
        visitorScore,
      })
    })

    return {
      localDefinitionScore,
      localScore,
      visitorDefinitionScore,
      visitorScore,
    }
  }

  static sortEvents(events) {
    return sortBy(events, [(a) => (a.type === 'PENAL_DEFINITION' ? 1 : 0), (a) => a.second || 0, (a) => a.report_id])
  }

  get status() {
    let status = 'todo'
    if (this.isApproved) {
      status = 'approved'
    } else if (this.isBothLose) {
      status = 'approved'
    } else if (this.dateEnded) {
      status = 'done'
    } else if ((this.started_at || this.matched_at) && !this.isDeferred) {
      const min = this.minutes

      if (min >= MAX_MATCH_MINUTES) {
        status = 'done'
      } else if (min > 0) {
        status = 'doing'
      }
    }
    return status
  }

  get time() {
    let seconds = 0

    if ((this.started_at || this.matched_at) && !this.isDeferred) {
      const start = parseInt(this.started_at || this.matched_at || 0, 10)
      const end = parseInt(this.ended_at || moment.utc().valueOf() / 1000, 10)

      seconds = Math.min(Math.max(end - start, 0), MAX_MATCH_MINUTES * 60)
    }

    return seconds
  }

  get waitingTime() {
    let seconds = 0

    if ((this.started_at || this.matched_at) && !this.isDeferred) {
      const start = parseInt(this.started_at || this.matched_at || 0, 10)
      const now = moment.utc().valueOf() / 1000

      seconds = Math.max(start - now, 0)
    }

    return seconds
  }
}
